import { Address } from "viem"

import type { ClFarmApiData } from "@/domains/cl-farm/types"
import type { IGaugeRepository } from "@/domains/gauge"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { TokenRepository } from "@/domains/tokens"
import type { WalletConnectionStore } from "@/domains/wallet"
import { ClFarmApiTransport } from "@/domains/cl-farm/cl-farm-api-transport"
import { ClFarmOnChainTransport } from "@/domains/cl-farm/cl-farm-on-chain-transport"
import { ClFarmOrchestrator } from "@/domains/cl-farm/cl-farm-orchestrator"
import { ClFarmRepository } from "@/domains/cl-farm/cl-farm-repository"
import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"
import { LogStore } from "@/domains/log/log-store"

export class ClFarmOrchestratorFactory {
  /**
   * Create a fully configured ClFarmOrchestrator with its OrchestratedQuery
   */
  static create(
    clFarmApiTransport: ClFarmApiTransport,
    clFarmOnChainTransport: ClFarmOnChainTransport,
    clFarmRepository: ClFarmRepository,
    orchestratedQueryStore: OrchestratedQueryStore,
    tokenRepository: TokenRepository,
    walletStore: WalletConnectionStore,
    log: LogStore,
    eventBus: IEventBus,
    gaugeRepository: IGaugeRepository | undefined,
    governanceTokenAddress: LcAddress,
  ): ClFarmOrchestrator {
    // 1. Create orchestrator with all dependencies
    const orchestrator = new ClFarmOrchestrator(
      clFarmApiTransport,
      clFarmOnChainTransport,
      clFarmRepository,
      tokenRepository,
      walletStore,
      log,
      eventBus,
      gaugeRepository,
      governanceTokenAddress,
    )

    // 2. Create farms sync query (loads the basic farms data from API)
    const loadFarmsQuery = new OrchestratedQuery<Array<ClFarmApiData>>(
      "CL Farms Sync",
      (query) => orchestrator.onLoadFarmsTrigger(query),
      86_400 * 1000 * 7, // weekly lol
      orchestrator.onLoadFarmsQuerySuccess.bind(orchestrator),
      orchestrator.onLoadFarmsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 3. Create user info sync query (calls the current lens onchain - will soon be split into two lenses)
    const loadUserInfoQuery = new OrchestratedQuery<any[]>(
      "CL Farms User Info Sync",
      (query) => orchestrator.onLoadUserInfoTrigger(query, walletStore),
      60_000,
      orchestrator.onLoadUserInfoQuerySuccess.bind(orchestrator),
      orchestrator.onLoadUserInfoQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
      undefined, // rateLimit
      { min: 1, max: 100, default: 25 }, // chunkSizeBounds
    )

    // 4. Create earned rewards sync query
    const loadEarnedRewardsQuery = new OrchestratedQuery<Array<{ farmAddress: Address; earned: bigint }>>(
      "CL Farms Earned Rewards Sync",
      (query) => orchestrator.onLoadEarnedRewardsTrigger(query, walletStore),
      30_000, // 30 seconds interval (same as user info)
      orchestrator.onLoadEarnedRewardsQuerySuccess.bind(orchestrator),
      orchestrator.onLoadEarnedRewardsQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 5. Initialize orchestrator with all queries
    orchestrator.initialize(loadFarmsQuery, loadUserInfoQuery, loadEarnedRewardsQuery)

    // 6. Do NOT Start the query - farms are generic and later will load user data on top
    // It must be started after the tokens have initialized (Add all CL Farms tokens to the token repository manually)
    // loadFarmsQuery.start()

    // loadUserInfoQuery.start()

    return orchestrator
  }
}
